import { getDeveloperDescriptionHtml, getDeveloperLogoUrl } from '@/helpers/developer';

// Which project tabs have something to show. Mirrors each tab's own empty state, so a tab
// without data is hidden instead of opening on "nothing here".

type RawDoc = { url?: string; src?: string };
type ProjectLike = {
  documents?: RawDoc[];
  brochures?: unknown[];
  floor_plans?: unknown[];
  videos?: unknown[];
  locks?: { documents?: { lockedCount?: number } };
  latitude?: unknown;
  longitude?: unknown;
  developer?: {
    logo?: string;
    description?: unknown;
    founded_at?: string;
    country?: { en?: string };
    delivered_units?: number | string | null;
    delivered_projects?: number | string | null;
  } | null;
};

const asProject = (property: unknown): ProjectLike => (property ?? {}) as ProjectLike;

export const hasDocumentsData = (property: unknown): boolean => {
  const p = asProject(property);
  // A Member's locked documents still belong on the tab (same rule as the app).
  if ((p.locks?.documents?.lockedCount ?? 0) > 0) return true;
  if (Array.isArray(p.documents) && p.documents.length) return p.documents.some((d) => !!(d.url || d.src));
  return !!(p.brochures?.length || p.floor_plans?.length || p.videos?.length);
};

// (0, 0) is the postgres mapper's "missing coordinates" value; nearby places also need coordinates.
export const hasLocationData = (property: unknown): boolean => {
  const p = asProject(property);
  const lat = Number(p.latitude);
  const lng = Number(p.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
};

export const hasDeveloperData = (property: unknown): boolean => {
  const p = asProject(property);
  const dev = p.developer;
  if (!dev) return false;
  return !!(
    getDeveloperLogoUrl(p as Parameters<typeof getDeveloperLogoUrl>[0]) ||
    getDeveloperDescriptionHtml(dev) ||
    dev.founded_at ||
    dev.country?.en ||
    dev.delivered_projects != null ||
    dev.delivered_units != null
  );
};

// Tabs of AccessTabsRow (guest and registered viewers) and the shell tab each one opens.
export const ACCESS_TO_INTERNAL_TAB: Record<string, string> = {
  general: 'overview',
  location: 'location',
  layouts: 'layouts',
  availability: 'availability',
  price: 'floorPlans',
  amenities: 'amenities',
  developer: 'developer',
  documents: 'documents',
};

const GUEST_ALWAYS_HIDDEN = ['availability', 'documents'];

// Every tab left visible can open: a tab with no shell tab behind it would be a silent no-op click.
// Locked tabs stay, because they open the verify prompt rather than the tab.
export const hiddenAccessTabs = (
  level: 'guest' | 'registered',
  internalTabs: string[],
  lockedTabs: string[],
): string[] => {
  const withoutData = Object.entries(ACCESS_TO_INTERNAL_TAB)
    .filter(([access, internal]) => !internalTabs.includes(internal) && !lockedTabs.includes(access))
    .map(([access]) => access);
  return level === 'guest' ? [...new Set([...GUEST_ALWAYS_HIDDEN, ...withoutData])] : withoutData;
};
